import { Change, Comment, Log, Quote } from '../../models';
import { Changes } from '../../services';
import { Dispatch } from 'redux';
import { createRestSlices, applyReducers } from './rest';
import { ActionPayload } from '..';
import type { Status, Callback, Response } from './rest';
import { entityId, mergePopulatedChangeRefs, removeFromList, sameAssigneeList, upsertList } from './remoteUpdate';
import { reloadBoardIfSmart } from './boards';
import { reportTrackStartError } from './tracks';

const {
    initialState, resetReducer,
    createReducer, startCreateReducer, cancelCreateReducer,
    getReducer, listReducer,
    updateReducer, deleteReducer,
    duplicateReducer,
    createAction, startCreateAction, cancelCreateAction,
    resetAction,
    getAction, listAction,
    updateAction, deleteAction,
    duplicateAction,
} = createRestSlices(Changes);

initialState.toStep         = 'idle' as Status;
initialState.toStepChangeId = null;
initialState.toStepError    = null;

initialState.addQuote         = 'idle' as Status;
initialState.addQuoteChangeId = null;
initialState.addQuoteError    = null;

initialState.reviewChanges = [];
initialState.triageChanges = [];
initialState.reviewChangesStatus = 'idle' as Status;
initialState.triageChangesStatus = 'idle' as Status;

const UPDATE = 'yoda/mercure/REMOTE_UPDATE';
const onRemoteUpdateReducer = (state = initialState, action: ActionPayload) => {
    if (action.type !== UPDATE)
        return state;

    const { update: { data, type }} = action;
    const patchChangeLists = (change: Change) => {
        const isDiscarded = change.progress?.status === Change.STATUS_DISCARDED;
        const insertInMyTasks = !isDiscarded && sameAssigneeList(state.changes, change);
        const triageInsert = change.progress?.status === Change.STATUS_PENDING_TRIAGE;
        const reviewInsert = change.progress?.status === Change.STATUS_TEAM_REVIEW;
        const asChange = (payload: AnyObj, list?: Change[]) => {
            const previous = (list || []).find((item: Change) => item._id === payload._id);
            return new Change(mergePopulatedChangeRefs(payload, previous));
        };
        return {
            changes: upsertList(
                state.changes,
                change,
                (payload) => asChange(payload, state.changes),
                insertInMyTasks
            ).filter((item: Change) => (
                item._id !== change._id || item.progress?.status !== Change.STATUS_DISCARDED
            )),
            triageChanges: upsertList(
                state.triageChanges,
                change,
                (payload) => asChange(payload, state.triageChanges),
                triageInsert
            ).filter((item: Change) => (
                item._id !== change._id || item.progress?.status === Change.STATUS_PENDING_TRIAGE
            )),
            reviewChanges: upsertList(
                state.reviewChanges,
                change,
                (payload) => asChange(payload, state.reviewChanges),
                reviewInsert
            ).filter((item: Change) => (
                item._id !== change._id || item.progress?.status === Change.STATUS_TEAM_REVIEW
            )),
        };
    };

    switch (type) {
        case 'change.updated':
        case 'change.created': {
            const previous = (state.change && state.change._id === data._id)
                ? state.change
                : (state.changes || []).find((item: Change) => item._id === data._id)
                    || (state.triageChanges || []).find((item: Change) => item._id === data._id)
                    || (state.reviewChanges || []).find((item: Change) => item._id === data._id)
                    || null;
            const change = new Change(mergePopulatedChangeRefs(data, previous));
            const lists  = patchChangeLists(change);
            return {
                ...state,
                ...lists,
                change: (state.change && state.change._id === change._id)
                    ? change
                    : state.change
            };
        }
        case 'change.deleted': {
            const id = data._id;
            return {
                ...state,
                changes: removeFromList(state.changes, id),
                triageChanges: removeFromList(state.triageChanges, id),
                reviewChanges: removeFromList(state.reviewChanges, id),
                change: state.change?._id === id ? null : state.change
            };
        }
        case 'change.merged': {
            const previousTarget = data.target && state.change?._id === data.target._id
                ? state.change
                : (data.target
                    ? (state.changes || []).find((item: Change) => item._id === data.target._id)
                        || (state.triageChanges || []).find((item: Change) => item._id === data.target._id)
                        || (state.reviewChanges || []).find((item: Change) => item._id === data.target._id)
                        || null
                    : null);
            const target  = data.target
                ? new Change(mergePopulatedChangeRefs(data.target, previousTarget))
                : null;
            const sources = (data.sources || []) as AnyObj[];
            let next = { ...state };
            if (target) {
                const lists = patchChangeLists(target);
                next = {
                    ...next,
                    ...lists,
                    change: (state.change && state.change._id === target._id) ? target : state.change
                };
            }
            sources.forEach((source: AnyObj) => {
                next.changes = removeFromList(next.changes, source._id);
                next.triageChanges = removeFromList(next.triageChanges, source._id);
                next.reviewChanges = removeFromList(next.reviewChanges, source._id);
                if (next.change?._id === source._id)
                    next.change = target;
            });
            return next;
        }
        case 'quote.created':
        case 'quote.updated':
            if (!state.change)
                return state;

            if (!data.items?.map((item: AnyObj) => item.change?._id).includes(state.change._id))
                return state;

            return {
                ...state,
                change: new Change({
                    ...state.change,
                    quote: new Quote(data)
                })
            };
        case 'comment.created': {
            if (!state.change)
                return state;
            if (entityId(data.change) !== String(state.change._id))
                return state;
            const already = (state.change.comments || []).some((comment: Comment) => comment._id === data._id);
            if (already)
                return state;
            return {
                ...state,
                change: new Change({
                    ...state.change,
                    comments: [...(state.change.comments || []), new Comment(data)]
                })
            };
        }
        case 'log.created': {
            if (!state.change)
                return state;
            if (entityId(data.change) !== String(state.change._id))
                return state;
            const already = (state.change.logs || []).some((log: Log) => log._id === data._id);
            if (already)
                return state;
            return {
                ...state,
                change: new Change({
                    ...state.change,
                    logs: [new Log(data), ...(state.change.logs || [])]
                })
            };
        }
        case 'comment.updated': {
            if (!state.change)
                return state;
            if (entityId(data.change) !== String(state.change._id))
                return state;
            return {
                ...state,
                change: new Change({
                    ...state.change,
                    comments: (state.change.comments || []).map((comment: Comment) => (
                        comment._id === data._id ? new Comment(data) : comment
                    ))
                })
            };
        }
        case 'comment.deleted': {
            if (!state.change)
                return state;
            if (entityId(data.change) !== String(state.change._id))
                return state;
            return {
                ...state,
                change: new Change({
                    ...state.change,
                    comments: (state.change.comments || []).filter((comment: Comment) => comment._id !== data._id)
                })
            };
        }
        default:
            return state;
    }
};

const TO_STEP         = 'yoda/change/TO_STEP';
const TO_STEP_SUCCESS = 'yoda/change/TO_STEP_SUCCESS';
const TO_STEP_FAILURE = 'yoda/change/TO_STEP_FAILURE';
const TO_STEP_RESET   = 'yoda/change/TO_STEP_RESET';

const CREATE_BUG_REPORT = 'yoda/change/CREATE_BUG_REPORT';
const CREATE_BUG_REPORT_SUCCESS = 'yoda/change/CREATE_BUG_REPORT_SUCCESS';
const CREATE_BUG_REPORT_FAILURE = 'yoda/change/CREATE_BUG_REPORT_FAILURE';

const ADD_CHANGE_TO_COLUMN = 'yoda/board/ADD_CHANGE_TO_COLUMN';
const ADD_CHANGE_TO_COLUMN_SUCCESS = 'yoda/board/ADD_CHANGE_TO_COLUMN_SUCCESS';
const ADD_CHANGE_TO_COLUMN_FAILURE = 'yoda/board/ADD_CHANGE_TO_COLUMN_FAILURE';

const GET_REVIEW_CHANGES         = 'yoda/change/GET_REVIEW_CHANGES';
const GET_REVIEW_CHANGES_SUCCESS = 'yoda/change/GET_REVIEW_CHANGES_SUCCESS';
const GET_REVIEW_CHANGES_FAILURE = 'yoda/change/GET_REVIEW_CHANGES_FAILURE';

const reviewChangesReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case GET_REVIEW_CHANGES:
            return {
                ...state,
                reviewChangesStatus: 'pending',
                reviewChangesError: null
            };
        case GET_REVIEW_CHANGES_SUCCESS:
            return {
                ...state,
                reviewChangesStatus: 'success',
                reviewChanges: action.changes,
                reviewChangesError: null
            };
        case GET_REVIEW_CHANGES_FAILURE:
            return {
                ...state,
                reviewChangesStatus: 'failed',
                reviewChangesError: action.error
            };
        default:
            return state;
    }
};

const GET_TRIAGE_CHANGES         = 'yoda/change/GET_TRIAGE_CHANGES';
const GET_TRIAGE_CHANGES_SUCCESS = 'yoda/change/GET_TRIAGE_CHANGES_SUCCESS';
const GET_TRIAGE_CHANGES_FAILURE = 'yoda/change/GET_TRIAGE_CHANGES_FAILURE';
const CLAIM_FROM_TRIAGE         = 'yoda/change/CLAIM_FROM_TRIAGE';
const CLAIM_FROM_TRIAGE_SUCCESS = 'yoda/change/CLAIM_FROM_TRIAGE_SUCCESS';
const CLAIM_FROM_TRIAGE_FAILURE = 'yoda/change/CLAIM_FROM_TRIAGE_FAILURE';

const triageChangesReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case GET_TRIAGE_CHANGES:
            return {
                ...state,
                triageChangesStatus: 'pending',
                triageChangesError: null
            };
        case GET_TRIAGE_CHANGES_SUCCESS:
            return {
                ...state,
                triageChangesStatus: 'success',
                triageChanges: action.changes,
                triageChangesError: null
            };
        case GET_TRIAGE_CHANGES_FAILURE:
            return {
                ...state,
                triageChangesStatus: 'failed',
                triageChangesError: action.error
            };
        default:
            return state;
    }
};

const claimFromTriageReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case CLAIM_FROM_TRIAGE:
            return {
                ...state,
                updateStatus: 'pending',
                updateError: null
            };
        case CLAIM_FROM_TRIAGE_SUCCESS:
            return {
                ...state,
                updateStatus: 'success',
                change: action.change,
                updateError: null
            };
        case CLAIM_FROM_TRIAGE_FAILURE:
            return {
                ...state,
                updateStatus: 'failed',
                updateError: action.error
            };
        default:
            return state;
    }
};

// Reducer pour gérer les états de création de rapport de bug
const bugReportReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case CREATE_BUG_REPORT:
            return {
                ...state,
                change: null,
                bugReportStatus: 'pending',
                bugReportError: null,
                bugReportId: action._id
            };
        case CREATE_BUG_REPORT_SUCCESS:
            return {
                ...state,
                bugReportStatus: 'success',
                change: action.change,
                bugReportError: null
            };
        case CREATE_BUG_REPORT_FAILURE:
            return {
                ...state,
                change: null,
                bugReportStatus: 'failed',
                bugReportError: action.error
            };
        default:
            return state;
    }
};

/**
 * We have to add this hook to handle comments creation/deletion
 */
const CREATING_COMMENT_SUCCESS = `yoda/comment/CREATING_ITEM_SUCCESS`;
const UPDATING_COMMENT_SUCCESS = `yoda/comment/UPDATING_ITEM_SUCCESS`;
const commentsReducer = (state = initialState, action: ActionPayload) => {
    if (!state.change)
        return state;

    switch (action.type) {
        case CREATING_COMMENT_SUCCESS:
            return {
                ...state,
                change: new Change({
                    ...state.change,
                    comments: [...state.change.comments, action.comment]
                })
            };
        case UPDATING_COMMENT_SUCCESS:
            const comments = state.change.comments?.map((comment: Comment) => {
                if (comment._id !== action.comment._id)
                    return comment;

                return action.comment;
            })
            return {
                ...state,
                change: new Change({
                    ...state.change,
                    comments
                })
            };
        default:
            return state;
    }
};


const toStepReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case TO_STEP:
            return {
                ...state,
                toStep: 'pending',
                toStepChangeId: action._id,
                toStepError: null
            };
        case TO_STEP_SUCCESS: {
            const change = action.change instanceof Change
                ? action.change
                : new Change(action.change);
            return {
                ...state,
                toStep: 'succeeded',
                toStepError: null,
                change,
                // Keep list consumers (e.g. dashboard Mes tâches) in sync with
                // the modal step transition — previously only `change` was updated.
                changes: (state.changes || []).map((item: Change) => (
                    item._id === change._id ? change : item
                )),
            };
        }
        case TO_STEP_FAILURE:
            return {
                ...state,
                toStep: 'failed',
                toStepError: action.error
            };
        case TO_STEP_RESET:
            return {
                ...state,
                toStep: 'idle',
                toStepChangeId: null,
                toStepError: null,
            };
        default:
            return state;
    }
};

const toStepAction = (_id: string, step: string, params?: AnyObj, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: TO_STEP, _id});
        return Changes.toStep(_id, step, params)
            .then((data: Response) => {
                const {change} = data;
                dispatch({type: TO_STEP_SUCCESS, change});
                dispatch(reloadBoardIfSmart() as any);
                if (data.trackError)
                    dispatch(reportTrackStartError(data.trackError));
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: TO_STEP_FAILURE, error: error.message});
                callback && callback(error);
            });
    }
};

/** Close without starting real work (same as triage Resolve). */
const resolveFromTriageAction = (_id: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: TO_STEP, _id});
        return Changes.resolveFromTriage(_id)
            .then((data: Response) => {
                const {change} = data;
                dispatch({type: TO_STEP_SUCCESS, change});
                dispatch(reloadBoardIfSmart() as any);
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: TO_STEP_FAILURE, error: error.message});
                callback && callback(error);
            });
    }
};

const boardUpdateReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case ADD_CHANGE_TO_COLUMN:
            return {
                ...state,
                updateStatus: 'pending',
                updateError: null
            };
        case ADD_CHANGE_TO_COLUMN_SUCCESS:
            return {
                ...state,
                updateStatus: 'success',
                board: action.updatedBoard,
                updateError: null
            };
        case ADD_CHANGE_TO_COLUMN_FAILURE:
            return {
                ...state,
                updateStatus: 'failed',
                updateError: action.error
            };
        default:
            return state;
    }
};

const ADD_QUOTE         = 'yoda/change/ADD_QUOTE';
const ADD_QUOTE_SUCCESS = 'yoda/change/ADD_QUOTE_SUCCESS';
const ADD_QUOTE_FAILURE = 'yoda/change/ADD_QUOTE_FAILURE';

const addQuoteReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case ADD_QUOTE:
            return {
                ...state,
                addQuote: 'pending',
                addQuoteChangeId: action._id,
                addQuoteError: null
            };
        case ADD_QUOTE_SUCCESS:
            return {
                ...state,
                addQuote: 'succeeded',
                addQuoteError: null,
                change: action.change
            };
        case ADD_QUOTE_FAILURE:
            return {
                ...state,
                addQuote: 'failed',
                addQuoteError: action.error
            };
        default:
            return state;
    }
};

const UPDATING_QUOTE_SUCCESS = 'yoda/quote/UPDATING_ITEM_SUCCESS';
const TO_QUOTE_STEP_SUCCESS  = 'yoda/quote/TO_STEP_SUCCESS';
const updateQuoteReducer = (state = initialState, action: ActionPayload) => {
    if (!state.change)
        return state;

    switch (action.type) {
        case TO_QUOTE_STEP_SUCCESS:
        case UPDATING_QUOTE_SUCCESS:
            const quote = action.quote;
            if (!quote.items.map((item: AnyObj) => item.change?._id).includes(state.change._id))
                return state;

            return {
                ...state,
                change: new Change({
                    ...state.change,
                    quote
                })
            };
        default:
            return state;
    }
};

const addQuoteAction = (_id: string, params?: AnyObj[], callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: ADD_QUOTE, _id});
        return Changes.addQuote(_id, params)
            .then((data: Response) => {
                const {change} = data;
                dispatch({type: ADD_QUOTE_SUCCESS, change});
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: ADD_QUOTE_FAILURE, error: error.message});
                callback && callback(error);
            });
    }
};

const reviewChangesAction = (params: AnyObj, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: GET_REVIEW_CHANGES });
        params = {
            ...params,
            'progress.status': Change.STATUS_TEAM_REVIEW,
            with: 'logs'
        };
        return Changes.list(params)
            .then((data: Response) => {
                const { changes } = data;
                dispatch({ type: GET_REVIEW_CHANGES_SUCCESS, changes });
                callback && callback(null, changes);
            })
            .catch((error: Error) => {
                dispatch({ type: GET_REVIEW_CHANGES_FAILURE, error: error.message });
                callback && callback(error);
            });
    };
};

const triageChangesAction = (params: AnyObj, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: GET_TRIAGE_CHANGES });
        params = {
            ...params,
            'progress.status': Change.STATUS_PENDING_TRIAGE,
            view: 'triage',
        };
        return Changes.list(params)
            .then((data: Response) => {
                const { changes } = data;
                dispatch({ type: GET_TRIAGE_CHANGES_SUCCESS, changes });
                callback && callback(null, changes);
            })
            .catch((error: Error) => {
                dispatch({ type: GET_TRIAGE_CHANGES_FAILURE, error: error.message });
                callback && callback(error);
            });
    };
};

const claimFromTriageAction = (_id: string, assignee?: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: CLAIM_FROM_TRIAGE, _id });
        return Changes.claimFromTriage(_id, assignee)
            .then((data: Response) => {
                const { change } = data;
                dispatch({ type: CLAIM_FROM_TRIAGE_SUCCESS, change });
                callback && callback(null, change);
            })
            .catch((error: Error) => {
                dispatch({ type: CLAIM_FROM_TRIAGE_FAILURE, error: error.message });
                callback && callback(error);
            });
    };
};

const DISCARD_CHANGE         = 'yoda/change/DISCARD_CHANGE';
const DISCARD_CHANGE_SUCCESS = 'yoda/change/DISCARD_CHANGE_SUCCESS';
const DISCARD_CHANGE_FAILURE = 'yoda/change/DISCARD_CHANGE_FAILURE';
const RESTORE_CHANGE         = 'yoda/change/RESTORE_CHANGE';
const RESTORE_CHANGE_SUCCESS = 'yoda/change/RESTORE_CHANGE_SUCCESS';
const RESTORE_CHANGE_FAILURE = 'yoda/change/RESTORE_CHANGE_FAILURE';

const discardRestoreReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case DISCARD_CHANGE:
        case RESTORE_CHANGE:
            return {
                ...state,
                deleting: action.type === DISCARD_CHANGE ? 'pending' : state.deleting,
                updateStatus: action.type === RESTORE_CHANGE ? 'pending' : state.updateStatus,
                deletingError: null,
                updateError: null,
            };
        case DISCARD_CHANGE_SUCCESS: {
            const id = action.change?._id;
            return {
                ...state,
                deleting: 'succeeded',
                deletingError: null,
                deleted: true,
                // Keep the discarded card so the modal can close cleanly (no Loader flash).
                change: action.change || state.change,
                changes: removeFromList(state.changes, id),
                triageChanges: removeFromList(state.triageChanges, id),
                reviewChanges: removeFromList(state.reviewChanges, id),
            };
        }
        case RESTORE_CHANGE_SUCCESS: {
            const restored = action.change;
            const insertInMyTasks = !!restored
                && restored.progress?.status !== Change.STATUS_DISCARDED
                && sameAssigneeList(state.changes, restored);
            const asChange = (payload: AnyObj) => (
                payload instanceof Change ? payload : new Change(payload)
            );
            return {
                ...state,
                updateStatus: 'success',
                updateError: null,
                change: restored,
                changes: upsertList(
                    state.changes,
                    restored,
                    asChange,
                    insertInMyTasks
                ).filter((item: Change) => (
                    item._id !== restored?._id || item.progress?.status !== Change.STATUS_DISCARDED
                )),
                triageChanges: restored?.progress?.status === Change.STATUS_PENDING_TRIAGE
                    ? upsertList(state.triageChanges, restored, asChange, true)
                    : removeFromList(state.triageChanges, restored?._id),
            };
        }
        case DISCARD_CHANGE_FAILURE:
            return {
                ...state,
                deleting: 'failed',
                deletingError: action.error,
                deleted: false,
            };
        case RESTORE_CHANGE_FAILURE:
            return {
                ...state,
                updateStatus: 'failed',
                updateError: action.error,
            };
        default:
            return state;
    }
};

const discardChangeAction = (_id: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: DISCARD_CHANGE, _id });
        return Changes.discardFromTriage(_id)
            .then((data: Response) => {
                const { change } = data;
                dispatch({ type: DISCARD_CHANGE_SUCCESS, change });
                callback && callback(null, change);
            })
            .catch((error: Error) => {
                dispatch({ type: DISCARD_CHANGE_FAILURE, error: error.message });
                callback && callback(error);
            });
    };
};

const restoreChangeAction = (_id: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: RESTORE_CHANGE, _id });
        return Changes.restoreFromTriage(_id)
            .then((data: Response) => {
                const { change } = data;
                dispatch({ type: RESTORE_CHANGE_SUCCESS, change });
                callback && callback(null, change);
            })
            .catch((error: Error) => {
                dispatch({ type: RESTORE_CHANGE_FAILURE, error: error.message });
                callback && callback(error);
            });
    };
};

const createBugReportAction = (id: string, params: AnyObj, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: CREATE_BUG_REPORT, id });

        return Changes.createBugReport(id, params)
            .then((change) => {
                dispatch({
                    type: CREATE_BUG_REPORT_SUCCESS, change
                });
                callback && callback(null, change);
            })
            .catch((error: Error) => {
                dispatch({
                    type: CREATE_BUG_REPORT_FAILURE,
                    error: error.message
                });

                callback && callback(error);
            });
    };
};

const addChangeToBoardColumnAction = (
    projectId: string,
    columnId: string,
    changeId: string,
    callback?: Callback
) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: ADD_CHANGE_TO_COLUMN, projectId });

        Changes.addChangeToColumn(projectId, columnId, changeId)
            .then((updatedBoard) => {
                dispatch({
                    type: ADD_CHANGE_TO_COLUMN_SUCCESS,
                    updatedBoard
                });
                if (callback) callback(null, updatedBoard);
            })
            .catch((error: Error) => {
                dispatch({
                    type: ADD_CHANGE_TO_COLUMN_FAILURE,
                    error: error.message
                });
                if (callback) callback(error);
            });
    };
};

const SET_IMAGE           = 'yoda/changes/SET_IMAGE';
const SET_IMAGE_SUCCESS   = 'yoda/changes/SET_IMAGE_SUCCESS';
const SET_IMAGE_FAILURE   = 'yoda/changes/SET_IMAGE_FAILURE';

initialState.setImage        = 'idle' as Status;
initialState.setImageSuccess = null;
initialState.setImageError   = null;

const setImageReleaseReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case SET_IMAGE:
            return {
                ...state,
                setImage: 'loading',
                setImageError: null
            };
        case SET_IMAGE_SUCCESS:
            return {
                ...state,
                setImageSuccess: 'success',
                change: action.change,
                setImageError: null
            };
        case SET_IMAGE_FAILURE:
            return {
                ...state,
                setImage: 'failure',
                setImageError: action.error
            };
        default:
            return state;
    }
};

const UNSET_IMAGE           = 'yoda/changes/UNSET_IMAGE';
const UNSET_IMAGE_SUCCESS   = 'yoda/changes/UNSET_IMAGE_SUCCESS';
const UNSET_IMAGE_FAILURE   = 'yoda/changes/UNSET_IMAGE_FAILURE';

initialState.unsetImage        = 'idle' as Status;
initialState.unsetImageSuccess = null;
initialState.unsetImageError   = null;
const unsetImageReleaseReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case UNSET_IMAGE:
            return {
                ...state,
                unsetImage: 'loading',
                unsetImageError: null
            };
        case UNSET_IMAGE_SUCCESS:
            return {
                ...state,
                unsetImageSuccess: 'success',
                change: action.change,
                unsetImageError: null
            };
        case UNSET_IMAGE_FAILURE:
            return {
                ...state,
                unsetImage: 'failure',
                unsetImageError: action.error
            };
        default:
            return state;
    }
};

const MOVE_CHANGE         = 'yoda/board/MOVE_CHANGE';
const MOVE_CHANGE_SUCCESS = 'yoda/board/MOVE_CHANGE_SUCCESS';
const MOVE_CHANGE_FAILURE = 'yoda/board/MOVE_CHANGE_FAILURE';
const moveChangeReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case MOVE_CHANGE:
            return {
                ...state,
                movingChange: 'pending',
                moveChangeError: null
            };
        case MOVE_CHANGE_SUCCESS:
            return {
                ...state,
                movingChange: 'success',
                change: action.change,
                moveChangeError: null
            };
        case MOVE_CHANGE_FAILURE:
            return {
                ...state,
                movingChange: 'failed',
                moveChangeError: action.error
            };
        default:
            return state;
    }
};
const moveChangeAction = (
    changeId: string,
    projectId: string,
    boardId: string,
    columnId: string,
    callback?: Callback
) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: MOVE_CHANGE });

        Changes.move(changeId, projectId, boardId, columnId)
            .then((data: Response) => {
                const {change} = data;
                dispatch({
                    type: MOVE_CHANGE_SUCCESS,
                    change
                });
                if (callback) callback(null, change);
            })
            .catch((error: Error) => {
                dispatch({
                    type: MOVE_CHANGE_FAILURE,
                    error: error.message
                });
                if (callback) callback(error);
            });
    };
};

/* Export reducer */
/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (state = initialState, action: ActionPayload) => {
    return applyReducers(state, action, [
        resetReducer,
        createReducer, startCreateReducer, cancelCreateReducer,
        getReducer, listReducer, onRemoteUpdateReducer,
        updateReducer, deleteReducer,
        duplicateReducer, toStepReducer,
        bugReportReducer, boardUpdateReducer,
        reviewChangesReducer, triageChangesReducer,
        claimFromTriageReducer,
        discardRestoreReducer,
        addQuoteReducer, updateQuoteReducer,
        commentsReducer,
        setImageReleaseReducer, unsetImageReleaseReducer,
        moveChangeReducer
    ]);
};

const setImageReleaseAction = (id: string, file: File, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: SET_IMAGE});
        return Changes.setImageRelease(id, file)
            .then((data: Response) => {
                dispatch({type: SET_IMAGE_SUCCESS, change: data?.change});
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: SET_IMAGE_FAILURE, error: error.message});
                callback && callback(error);
            });
    }
};

const unsetImageReleaseAction = (id: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: SET_IMAGE});
        return Changes.unsetImageRelease(id)
            .then((data: AnyObj) => {
                const { change } = data;
                dispatch({type: SET_IMAGE_SUCCESS, change});
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: SET_IMAGE_FAILURE, error: error.message});
                callback && callback(error);
            });
    }
}
/* Export CRUD actions */
export const resetChanges           = resetAction;
export const createChange           = createAction;
export const startCreateChange      = startCreateAction;
export const cancelCreateChange     = cancelCreateAction;
export const loadChange             = getAction;
export const loadChanges            = listAction;
export const updateChange           = (item: AnyObj, patch?: boolean, optimistic?: boolean, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        return dispatch(updateAction(item, patch, optimistic, (err, result) => {
            if (!err)
                dispatch(reloadBoardIfSmart() as any);
            callback && callback(err, result);
        }) as any);
    };
};
export const deleteChange           = deleteAction;
export const discardChange          = discardChangeAction;
export const restoreChange          = restoreChangeAction;
export const duplicateChange        = duplicateAction;
export const toChangeStep           = toStepAction;
export const resolveChangeFromTriage = resolveFromTriageAction;
export const addChangeQuote         = addQuoteAction;
export const createBugReport        = createBugReportAction;
export const addChangeToBoardColumn = addChangeToBoardColumnAction;
export const getReviewChanges       = reviewChangesAction;
export const getTriageChanges       = triageChangesAction;
export const setImageReleaseNotes   = setImageReleaseAction;
export const unsetImageReleaseNotes = unsetImageReleaseAction;
export const moveChange             = moveChangeAction;
export const claimFromTriage        = claimFromTriageAction;
